import React from 'react';
import css from './sections/sections.css';
import { resolveStatusByMatchingMatrixScore } from '../../util/user';
import config from '../../config';

const {
    listingTypeHorse,
    listingTypePrivateHorse,
    listingTypeRider,
    listingTypePrivateRider,
} = config;

export const distinguishBetweenMatchingEntries = (entriesDictionary, entriesConfigs) => {
    const { matchingEntries, nonMatchingEntries } = Object.entries(entriesDictionary).reduce(
        (acc, entry) => {
            const [_, value] = entry;
            const targetKey = value.matches ? 'matchingEntries' : 'nonMatchingEntries';

            acc[targetKey].push(entry);
            return acc;
        },
        { matchingEntries: [], nonMatchingEntries: [] }
    );

    const sortByConfigPosition = ([keyA], [keyB]) => {
        const indexA = entriesConfigs.findIndex(item => item.label === keyA);
        const indexB = entriesConfigs.findIndex(item => item.label === keyB);

        const posA = indexA === -1 ? Infinity : indexA;
        const posB = indexB === -1 ? Infinity : indexB;

        return posA - posB;
    };

    return {
        matchingEntries: matchingEntries.sort(sortByConfigPosition),
        nonMatchingEntries: nonMatchingEntries.sort(sortByConfigPosition),
    };
};

export const getRiderAndHorseListings = ({ currentPageListing, viewerListing }) => {
    const { riderListing, horseListing } = [currentPageListing, viewerListing].reduce(
        (acc, listing) => {
            const { type } = listing?.attributes?.publicData || {};

            const isHorseListing = type === listingTypeHorse || type === listingTypePrivateHorse;
            const isRiderListing = type === listingTypeRider || type === listingTypePrivateRider;

            if (isHorseListing) {
                return {
                    ...acc,
                    horseListing: listing,
                };
            }

            if (isRiderListing) {
                return {
                    ...acc,
                    riderListing: listing,
                };
            }

            return acc;
        },
        {}
    );

    return { riderListing, horseListing };
};

export const getEntriesDictionary = (acc, { entry, image }) => {
    const currentEntryData = acc[entry];

    if (currentEntryData) {
        return {
            ...acc,
            [entry]: { matches: true, images: [currentEntryData.images[0], image] },
        };
    }
    return {
        ...acc,
        [entry]: { matches: false, images: [image] },
    };
};

/**
 * @param {Object} params
 * @param {'high' | 'mid' | 'low'} params.categorySelected - high, mid, low
 * @param {number} params.total
 * @param {number} params.maxScore
 */
export const showSection = ({ categorySelected, total, maxScore }) => {
    const score = total === 0 || maxScore === 0 ? 0 : (total / maxScore) * 100;
    const scoreStatus = resolveStatusByMatchingMatrixScore(score);

    const isHighScore = scoreStatus === 'total';
    const isLowScore = scoreStatus === 'insufficient';
    const isMidScore = scoreStatus === 'sufficient';

    return {
        isHighScore,
        isLowScore,
        isMidScore,
        shouldBeShown: categorySelected
            ? (categorySelected === 'high' && isHighScore) ||
              (categorySelected === 'mid' && isMidScore) ||
              (categorySelected === 'low' && isLowScore)
            : true,
    };
};

export const resolveLocationDescriptionRider = ({
    isHighScore,
    isMidScore,
    isLowScore,
    distanceInKM,
    title,
}) => {
    if (isHighScore) {
        return (
            <span>
                Du und der Stall seid <span className={css.highlightText}>weniger als 15 km</span>{' '}
                voneinander entfernt.
            </span>
        );
    }
    if (isHighScore && distanceInKM >= 30) {
        return (
            <span>
                Du und der Stall seid{' '}
                <span className={css.highlightText}>zwischen 15 km und 50 km</span> vorneinander
                entfernt.
            </span>
        );
    }
    if (isMidScore && distanceInKM >= 50) {
        return (
            <span>
                Du und der Stall seid <span className={css.highlightText}>mehr als 50 km</span>{' '}
                voneinander entfernt.
            </span>
        );
    }
    if (isMidScore) {
        return (
            <span>
                Du und der Stall seid{' '}
                <span className={css.highlightText}>zwischen 15 km und 30 km</span> vorneinander
                entfernt.
            </span>
        );
    }
    if (isLowScore) {
        return (
            <span>
                Du und der Stall seid <span className={css.highlightText}>mehr als 30 km</span>{' '}
                voneinander entfernt.
            </span>
        );
    }
    void title;
};

export const resolveLocationDescriptionHorse = ({
    isHighScore,
    isMidScore,
    isLowScore,
    distanceInKM,
    title,
}) => {
    if (isHighScore) {
        return (
            <span>
                {title} und der Stall sind{' '}
                <span className={css.highlightText}>weniger als 15 km</span> voneinander entfernt.
            </span>
        );
    }
    if (isMidScore && distanceInKM >= 30) {
        return (
            <span>
                {title} und der Stall sind{' '}
                <span className={css.highlightText}>zwischen 15 km und 30 km</span> voneinander
                entfernt.
            </span>
        );
    }
    if (isMidScore) {
        return (
            <span>
                {title} und der Stall sind{' '}
                <span className={css.highlightText}>zwischen 15 km und 50 km</span> voneinander
                entfernt.
            </span>
        );
    }
    if (isLowScore && distanceInKM >= 50) {
        return (
            <span>
                {title} und der Stall sind <span className={css.highlightText}>mehr als 50 km</span>{' '}
                voneinander entfernt.
            </span>
        );
    }
    if (isLowScore) {
        return (
            <span>
                {title} und der Stall sind <span className={css.highlightText}>mehr als 30 km</span>{' '}
                voneinander entfernt.
            </span>
        );
    }
};

export const resolveRadiusDescriptionRider = ({ isHighScore, isLowScore, radius, title }) => {
    if (isHighScore) {
        return (
            <span>
                Du und der Stall seid{' '}
                <span className={css.highlightText}>weniger als {radius} km</span> voneinander
                entfernt.
            </span>
        );
    }

    if (isLowScore) {
        return (
            <span>
                Du und der Stall seid{' '}
                <span className={css.highlightText}>mehr als {radius} km</span> voneinander
                entfernt.
            </span>
        );
    }
    void title;
};

export const resolveRadiusDescriptionHorse = ({ isHighScore, isLowScore, radius, title }) => {
    if (isHighScore) {
        return (
            <span>
                {title} und der Stall sind{' '}
                <span className={css.highlightText}>weniger als {radius} km</span> voneinander
                entfernt.
            </span>
        );
    }

    if (isLowScore) {
        return (
            <span>
                {title} und der Stall sind{' '}
                <span className={css.highlightText}>mehr als {radius} km</span> voreinander
                entfernt.
            </span>
        );
    }
};

export const resolveMobilityDescriptionRider = ({ isHighScore, isLowScore, transport, title }) => {
    if (isHighScore) {
        return (
            <span>
                Du bist ist mit dem <span className={css.highlightText}>{transport}</span>{' '}
                unterwegs, das passt perfekt mit der Lage des Stalls zusammen.
            </span>
        );
    }

    if (isLowScore) {
        return (
            <span>
                Du bist mit den{' '}
                <span className={css.highlightText}>Öffentlichen Verkehrsmitteln</span> unterwegs
                und das passt leider nicht zur Lage des Stalls.
            </span>
        );
    }
    void title;
};

export const resolveMobilityDescriptionHorse = ({ isHighScore, isLowScore, transport, title }) => {
    if (isHighScore) {
        return (
            <span>
                {title} ist mit dem <span className={css.highlightText}>{transport}</span>{' '}
                unterwegs, das passt perfekt mit der Lage des Stalls zusammen.
            </span>
        );
    }

    if (isLowScore) {
        return (
            <span>
                {title} ist mit den{' '}
                <span className={css.highlightText}>Öffentlichen Verkehrsmitteln</span> unterwegs
                und das passt leider nicht zur Lage des Stalls.
            </span>
        );
    }
    void title;
};

export const resolveMinimumAgeDescriptionRider = ({
    isHighScore,
    isMidScore,
    isLowScore,
    minimumAge,
    title,
    age,
}) => {
    if (isHighScore) {
        return (
            <span>
                Dein Alter erfüllt das Mindestalter von{' '}
                <span className={css.highlightText}>{minimumAge} Jahren</span>.
            </span>
        );
    }

    if (isMidScore) {
        return (
            <span>
                Dein Alter weicht leicht vom Mindestalter von{' '}
                <span className={css.highlightText}>{minimumAge} Jahren</span> ab.
            </span>
        );
    }

    if (isLowScore) {
        return (
            <span>
                Dein Alter weicht deutlich vom Mindestalter von{' '}
                <span className={css.highlightText}>{minimumAge} Jahren</span> ab.
            </span>
        );
    }
    void title;
    void age;
};

export const resolveMinimumAgeDescriptionHorse = ({
    isHighScore,
    isMidScore,
    isLowScore,
    minimumAge,
    title,
    age,
}) => {
    if (isHighScore) {
        return (
            <span>
                Das Alter von {title} erfüllt das Mindestalter von{' '}
                <span className={css.highlightText}>{minimumAge} Jahren</span>.
            </span>
        );
    }

    if (isMidScore) {
        return (
            <span>
                {title} ist <span className={css.highlightText}>{age} Jahre</span> alt und weicht
                damit leicht vom Mindestalter von{' '}
                <span className={css.highlightText}>{minimumAge} Jahren</span> ab.
            </span>
        );
    }

    if (isLowScore) {
        return (
            <span>
                {title} ist <span className={css.highlightText}>{age} Jahre</span> alt und weicht
                damit deutlich vom Mindestalter von{' '}
                <span className={css.highlightText}>{minimumAge} Jahren</span> ab.
            </span>
        );
    }
};
